<template>
	<v-snackbar
		v-model="toast.isVisible"
		:timeout="
			toast.currentToast.timeout === null ? -1 : toast.currentToast.timeout
		"
		:color="toast.currentToast.color"
		:max-width="toast.currentToast.maxWidth || defaultMaxWidth()"
		:location="snackbarPosition || defaultPosition"
		:theme="toast.currentToast.theme"
		:timer="toast.currentToast.timeout !== null ? 'white' : undefined"
		variant="elevated"
		class="toast-wrapper"
		@update:model-value="(val) => !val && toast.hideToast()"
	>
		<div class="toast-content">
			<div
				v-if="toast.currentToast.header"
				class="text-subtitle-1 font-weight-bold mb-1"
			>
				{{ translate(toast.currentToast.header) }}
			</div>
			<div
				class="toast-message"
				v-html="translate(toast.currentToast.message)"
				v-if="toast.currentToast.is_html"
			></div>
			<div class="toast-message" v-else>
				{{ translate(toast.currentToast.message) }}
			</div>
		</div>

		<template #actions>
			<v-btn
				v-for="(button, index) in toast.currentToast.buttons"
				:key="index"
				variant="elevated"
				:color="button.color"
				@click="handleButtonClick(button)"
				:data-test="button.dataTest ? button.dataTest : 'toast-button'"
				class="mx-1"
				size="small"
			>
				{{ translate(button.text) }}
			</v-btn>
		</template>
	</v-snackbar>
</template>

<script lang="ts" setup>
import { computed, watch, onBeforeUnmount, ref } from 'vue'
import { useToastStore } from '@/stores/system/toast.ts'
import { useDisplay } from 'vuetify'
import type { Anchor } from 'vuetify/lib/util/anchor.d.ts'
import { useI18n } from 'vue-i18n'
import { useTranslate } from 'composables/useTranslate.ts'

const toast = useToastStore()
const display = useDisplay()

const { translate } = useTranslate()

const snackbarPosition = computed(() => {
	return toast.currentToast.position || display.mdAndUp
		? 'center center'
		: 'top center'
})

const handleButtonClick = (button: any) => {
	if (typeof button.on === 'function') {
		button.on()
	}
	toast.hideToast()
}

let escapeHandler: ((e: KeyboardEvent) => void) | null = null

const defaultPosition = computed(() => {
	return display.mdAndUp.value ? 'center center' : 'top center'
}) as unknown as Anchor

const defaultMaxWidth = () => {
	return display.mdAndUp.value ? '50%' : '90%'
}

const escapeHandlerRef = ref<((e: KeyboardEvent) => void) | null>(null)

watch(
	() => toast.isVisible,
	(current, previous) => {
		if (escapeHandlerRef.value) {
			document.removeEventListener('keydown', escapeHandlerRef.value)
			escapeHandlerRef.value = null
		}

		if (current && toast.currentToast.closeOnEscape) {
			escapeHandlerRef.value = (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					toast.hideToast()
				}
			}

			document.addEventListener('keydown', escapeHandlerRef.value)
		} else if (!current && previous) {
			toast.hideToast()
		}
	},
	{
		immediate: true,
	}
)

onBeforeUnmount(() => {
	if (escapeHandler) {
		document.removeEventListener('keydown', escapeHandler)
		escapeHandler = null
	}
})
</script>

<style scoped>
.toast-wrapper {
	pointer-events: auto;
}

.toast-content {
	white-space: pre-wrap;
}
</style>
